import { get, chain, map, some, find, size, capitalize, includes } from "lodash"
import { createSelector } from "reselect"
import { Creators as SegmentsCreators } from "modules/compare-segments-root/model"
import { getAllLongShortData } from "modules/compare-segments-root/selectors"
import { getSecurities, ITEMS_COLORS, getMetricFromComparisonType } from "helpers/comparison"
import { getUserPreference } from "helpers/userPreferences"
import { computePositionSet } from "../../../helpers/securities"
import { getSegmentLabel } from "helpers/segments"
import { getIndices } from "selectors/indices"
import { getItemActions } from "modules/comparison/helpers"
import { getViewType, getPortfolioName } from "modules/portfolio-root/selectors/portfolios"
import { getRawTopic } from "modules/research-topic-root/selectors"
import { getSelectedComparisonType as getTopicComparisonType } from "modules/research-topic/selectors"
import { getSavedMetrics as getExperimentSavedMetrics, getSelectedComparisonType as getExperimentSelectedComparisonType } from "modules/experiments/selectors"
import { getSelectedDate as getTopicSelectedDate } from "modules/research-topic/selectors/dates"
import { getSelectedMetric as getSelectedCompositionMetric } from "modules/compare-composition/selectors/metrics"
import { getSelectedMetric as getSelectedRiskMetric } from "modules/compare-risk/selectors/metrics"
import { getSelectedMetric as getSelectedExposureMetric } from "modules/compare-exposure/selectors/metrics"
import { getSelectedMetric as getSelectedPerformanceMetric } from "modules/compare-performance/selectors/metrics"

const EMPTY_OBJECT = {}

export const getCurrentPositionSet = (state) => get(state, "global.data.portfolio.data")
const getRawSecurities = (state) => get(state, "global.data.securitiesDescriptors.data.model")
const getBenchmarksAndPortfolios = (state) => get(state, "global.data.benchmarksAndPortfolios.data.model")
const getRawComparisonPreferences = (state) => get(state, "global.data.comparisonPreferences.data")
const getPortfolioId = (state) => get(state, "global.selected.portfolio")
const getAccount = (state) => get(state, "global.selected.account")

const getCoverageData = (n) => state => get(state, `global.data.comparisonItemCoverage${n}.data.model.simulation.coverage`)

export const getRawComparisonItems = state => get(state, "modules.comparison.comparisonItems")
export const getSavedMetrics = (state) => get(state, "modules.comparison.savedMetrics")
export const getSavedCompositionGroup = (state) => get(state, "modules.comparison.savedCompositionGroup")
export const getSelectedComparisonType = (state) => get(state, "modules.comparison.selectedComparisonType", EMPTY_OBJECT)

export const getExperimentId = props => get(props, "params.experimentId", null)
export const getSelectedExperiment = state => get(state, "modules.editExperiment.experiment", null)
export const getIndex = state => get(state, "modules.editExperiment.index", null)
export const getStatus = state => get(state, "modules.editExperiment.status", null)

const getStartDate = (_, props) => get(props, "startDate")
const getEndDate = (_, props) => get(props, "endDate")
const getSelectedDate = (_, props) => get(props, "selectedDate")
const getPathname = () => window.location.pathname

export const getBaseUrl = createSelector(
  [getPortfolioId, getAccount],
  (portfolioId, account) => `/${account}/p/${portfolioId}/analyze/compare`,
)

const getModeledItems = createSelector(
  [getCoverageData(1), getCoverageData(2), getCoverageData(3), getCoverageData(4)],
  (data1, data2, data3, data4) => map([data1, data2, data3, data4], data => some(data, elem => get(elem, "summary.percentGmvAvailable", 0) !== 0)))

export const getSelectedSettings = createSelector(
  [getRawComparisonItems, getSavedMetrics, getSavedCompositionGroup, getSelectedComparisonType],
  (comparisonItems, savedMetrics, savedCompositionGroup, selectedComparisonType) => ({
    comparisonItems,
    selectedComparisonType,
    savedMetrics,
    savedCompositionGroup,
  }),
)

export const getComparisonPreferences = createSelector(
  [getRawComparisonPreferences],
  preferences => getUserPreference(preferences, "compareSettings", {}),
)

export const SEARCH_ITEMS_ACTIONS = {
  benchmark: {
    mainAction: item => () => SegmentsCreators.addComparisonItem(item, null, "compare-add-comparison"),
  },
  index: {
    mainAction: item => () => SegmentsCreators.addComparisonItem(item, null, "compare-add-comparison"),
  },
  security: {
    mainAction: item => () => SegmentsCreators.addComparisonItem(item, null, "compare-add-comparison"),
  },
  portfolio: {
    mainAction: item => () => SegmentsCreators.addComparisonItem(item, null, "compare-add-comparison"),
  },
}

export const basePositionSets = createSelector(
  [getRawComparisonItems, getCurrentPositionSet, getRawSecurities, getBenchmarksAndPortfolios, getIndices, getModeledItems],
  (comparisonItems, currentPositionSet, securities, { benchmarks, portfolios }, indexes, modeledItems) => {
    const items = [
      {
        id: currentPositionSet.id,
        type: "portfolio",
        label: currentPositionSet.name,
        // We need a copy of the original name because
        // setting hedge options changes the label itself
        name: currentPositionSet.name,
        color: ITEMS_COLORS[0],
        initialItem: true,
      },
      ...map(comparisonItems, (item, i) => {
        const itemData = {
          ...item,
          id: item.id,
          type: item.type,
          color: ITEMS_COLORS[i + 1],
          uploading: !!item.uploading,
          isUnmodeled: !modeledItems || !modeledItems[i],
          evaluateAs: item.evaluateAs || "long",
        }

        switch (item.type) {
          case "security":
            itemData.label = get(securities, `[${item.ticker}${item.mic}]descriptors.name`, item.label)
            itemData.ticker = item.ticker
            itemData.mic = item.mic
            itemData.internalId = item.internalId
            itemData.assetClass = item.assetClass
            itemData.modelProviderId = item.modelProviderId
            break
          case "swap":
            itemData.label = item.label
            itemData.ticker = item.id
            itemData.assetClass = "swap"
            break
          case "benchmark":
            itemData.label = get(find(benchmarks, { id: item.id }), "name", item.label)
            break
          case "index":
            itemData.label = get(find(indexes, { id: item.id }), "name", item.label)
            break
          default:
            itemData.label = get(find(portfolios, { id: item.id }), "name", item.label)
            break
        }

        return itemData
      }),
    ]

    return chain(items)
      .map(item => {
        const positionSet = { ...item }

        if (positionSet.type === "security" || positionSet.type === "swap") {
          positionSet.positionSet = computePositionSet(positionSet.assetClass, positionSet.ticker, positionSet.mic, positionSet.modelProviderId, positionSet.evaluateAs, positionSet.internalId)
        }

        return positionSet
      })
      .value()
  })

export const getPositionSets = createSelector(
  [basePositionSets, getAllLongShortData],
  (positionSets, longShortData) => map(positionSets, (item, i) => ({
    ...item,
    label: getSegmentLabel(item.segment, item),
    actions: getItemActions(item, size(positionSets), longShortData[i]),
  })),
)

export const getComparisonSecurities = createSelector(
  [getPositionSets],
  getSecurities,
)

const getSelectedTopicMetric = createSelector(
  [getTopicComparisonType, getSelectedCompositionMetric, getSelectedRiskMetric, getSelectedExposureMetric, getSelectedPerformanceMetric],
  getMetricFromComparisonType,
)

const getSelectedPortfolioMetric = createSelector(
  [getSelectedComparisonType, getSelectedCompositionMetric, getSelectedRiskMetric, getSelectedExposureMetric, getSelectedPerformanceMetric, getExperimentSelectedComparisonType],
  getMetricFromComparisonType,
)

export const getCurrentLocation = createSelector(
  [getPathname],
  (pathname) => {
    switch (true) {
      case includes(pathname, "/smart-trade/"):
        return "smart-trade"
      case includes(pathname, "/t/"):
        return "topic"
      case includes(pathname, "rebalance/experiments"):
        return "experiment"
      default:
        return "portfolio"
    }
  },
)

const getPortfolioFileName = createSelector(
  [getPortfolioName, getViewType, getSelectedComparisonType, getSelectedPortfolioMetric, getExperimentSavedMetrics, getExperimentSelectedComparisonType, getSelectedDate, getStartDate, getEndDate, getCurrentLocation],
  (portfolioName, viewType, selectedComparisonType, selectedPortfolioMetric, experimentSavedMetrics, experimentSelectedComparisonType, selectedDate, startDate, endDate, downloadType) => {
    const { label } = selectedComparisonType ?? {}
    const metricLabel = get(selectedPortfolioMetric, "label")
    const comparisonType = label || get(experimentSelectedComparisonType, "label")
    const metric = metricLabel || get(experimentSavedMetrics, experimentSelectedComparisonType.id)
    return {
      chart: `${portfolioName} ${comparisonType} ${capitalize(viewType)} ${capitalize(metric)} ${capitalize(downloadType)} Comparison Chart ${startDate} - ${endDate}`,
      table: `${portfolioName} ${comparisonType} ${capitalize(viewType)} ${capitalize(downloadType)} Comparison Table ${selectedDate}`,
    }
  },
)

export const getTopicFileName = createSelector(
  [getRawTopic, getViewType, getTopicComparisonType, getSelectedTopicMetric, getSelectedDate, getTopicSelectedDate, getStartDate, getEndDate],
  (rawTopic, viewType, selectedComparisonType, selectedTopicMetric, selectedDate, topicSelectedDate, startDate, endDate) => {
    const comparisonType = get(selectedComparisonType, "label")
    const topicName = get(rawTopic, "name")
    const metric = get(selectedTopicMetric, "label")
    return {
      chart: `${topicName} ${comparisonType} ${capitalize(viewType)} ${capitalize(metric)} Research Topic Comparison Chart ${startDate} - ${endDate}`,
      table: `${topicName} ${comparisonType} ${capitalize(viewType)} Research Topic Comparison Table ${selectedDate || topicSelectedDate}`,
    }
  },
)

export const getFileName = createSelector(
  [getPortfolioFileName, getTopicFileName, getCurrentLocation],
  (portfolioFileName, topicFileName, downloadType) => {
    if (downloadType === "topic") {
      return {
        chart: topicFileName.chart,
        table: topicFileName.table,
      }
    }
    return {
      chart: portfolioFileName.chart,
      table: portfolioFileName.table,
    }
  })
